import React, { useState, useMemo, useEffect, useCallback } from 'react'

import {
  SurfaceCard,
  Stack,
  HStack,
  Typography,
  Button,
  DataGrid,
  TColumnDef,
  useToast,
  EmptyState,
  Pagination,
} from '@pnld/components-web'

import {
  useProcessingCollections,
  useProcessingCollectionsMetrics,
  useExportProcessingCollections,
  useProcessingCollectionsFilters,
} from '@/api/processing/queries'
import { ServiceMapFilterParams } from '@/api/processing/types'
import DataFilter, { IFilter, TFilter } from '@/components/DataFilter'
import TotalizerDisplay from '@/components/Display/Totalizer'
import { ProcessingCollection } from '@/types/processing'
import { getErrorMessage } from '@/utils/api'

import {
  serviceMapFilters,
  serviceMapFilterState,
  withServiceMapColumns,
} from '../../serviceMap'
import { PrintingCardProps } from './types'

// PSV-426: com mapa a tabela ganha 3 colunas (Etapas Atendidas pode ser
// longa) e passa a rolar na horizontal; sem mapa os filhos saem como estão.
const ServiceMapTableScroll: React.FC<{
  hasServiceMap: boolean
  children: React.ReactNode
}> = ({ hasServiceMap, children }) =>
  hasServiceMap ? (
    <Stack overflowX="auto" style={{ WebkitOverflowScrolling: 'touch' }}>
      <Stack minW="1400px">{children}</Stack>
    </Stack>
  ) : (
    <>{children}</>
  )

const PrintingCard: React.FC<PrintingCardProps> = ({
  processingId,
  hasServiceMap = false,
}) => {
  const toast = useToast()

  const [pagination, setPagination] = useState<{
    pageIndex: number
    pageSize: number
  }>({ pageIndex: 0, pageSize: 10 })

  const [filtersState, setFiltersState] = useState<
    {
      search?: string
      initialDate?: string
      finalDate?: string
      dsCollectionCode?: string
      dsPublisher?: string
      coComponent?: number
    } & ServiceMapFilterParams
  >({})

  const parseDate = useCallback((value?: string | null) => {
    if (!value) {
      return undefined
    }

    const [day, month, year] = value.split('/')
    if (!day || !month || !year) {
      return undefined
    }

    return `${year}-${month}-${day}`
  }, [])

  const handleOnChangeFilter = (filter: TFilter) => {
    const nextFilters = {
      search: filter.search || undefined,
      initialDate: parseDate(filter.startDate as string | null),
      finalDate: parseDate(filter.finalDate as string | null),
      dsCollectionCode: filter.dsCollectionCode || undefined,
      dsPublisher: filter.dsPublisher || undefined,
      coComponent: filter.coComponent ? Number(filter.coComponent) : undefined,
      ...serviceMapFilterState(filter),
    }

    setFiltersState(nextFilters)
  }

  const listPayload = useMemo(
    () => ({
      processingId: processingId as number,
      page: pagination.pageIndex + 1,
      qnt: pagination.pageSize,
      ...filtersState,
    }),
    [processingId, pagination, filtersState]
  )

  const { data: listData } = useProcessingCollections(listPayload, {
    keepPreviousData: true,
  })
  const { data: serverFiltersData } = useProcessingCollectionsFilters(
    { processingId: processingId as number },
    { enabled: Boolean(processingId) }
  )
  const { data: metricsData } = useProcessingCollectionsMetrics(
    {
      processingId: processingId as number,
      ...filtersState,
    },
    {
      enabled: Boolean(processingId),
    }
  )
  const { refetch: refetchExport, isFetching: isExporting } =
    useExportProcessingCollections(
      { processingId: processingId as number, ...filtersState },
      { enabled: false }
    )

  useEffect(() => {
    setPagination(currentPagination =>
      currentPagination.pageIndex === 0
        ? currentPagination
        : { ...currentPagination, pageIndex: 0 }
    )
  }, [filtersState])

  const handleExport = useCallback(async () => {
    try {
      const { data } = await refetchExport()

      if (!data) {
        toast({
          status: 'error',
          title: 'Ops! Ocorreu uma falha durante a exportação.',
        })
        return
      }

      const blobUrl = window.URL.createObjectURL(data)
      const link = document.createElement('a')

      link.href = blobUrl
      link.download = `processamento_${processingId}_tiragem.csv`
      document.body.appendChild(link)
      link.click()
      link.remove()

      toast({
        status: 'success',
        title: 'Exportação concluída com sucesso!',
      })

      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 5000)
    } catch (error) {
      toast({
        status: 'error',
        title: getErrorMessage(error),
      })
    }
  }, [refetchExport, processingId, toast])

  const numberFormatter = (value: number) =>
    value.toLocaleString('pt-BR', { minimumFractionDigits: 0 })

  const totals = useMemo(
    () => ({
      nuQuantityCensus: metricsData?.nuQuantityCensus || 0,
      nuQuantityQt1: metricsData?.nuQuantityQt1 || 0,
      nuQuantityQt2: metricsData?.nuQuantityQt2 || 0,
    }),
    [metricsData]
  )

  const baseColumns: Array<TColumnDef<ProcessingCollection>> = useMemo(
    () => [
      {
        id: 'collection',
        header: 'Coleção',
        enableSorting: false,
        cell: ({ row }) => (
          <Stack spacing={0.25}>
            <Typography variant="s-xs">{row.original.noCollection}</Typography>
            <Typography variant="s-xs" color="neutral.dark_2">
              {row.original.dsCollectionCode}
            </Typography>
          </Stack>
        ),
      },
      {
        accessorKey: 'dsPublisher',
        header: 'Editora',
        enableSorting: false,
      },
      {
        accessorKey: 'dsComponent',
        header: 'Componente',
        enableSorting: false,
      },
      {
        accessorKey: 'nuQuantityCensus',
        header: 'Quant. Censo',
        enableSorting: false,
        cell: ({ row }) => numberFormatter(row.original.nuQuantityCensus),
      },
      {
        accessorKey: 'nuQuantityQt1',
        header: 'Quant. QT1',
        enableSorting: false,
        cell: ({ row }) => numberFormatter(row.original.nuQuantityQt1),
      },
      {
        accessorKey: 'nuQuantityQt2',
        header: 'Quant. QT2',
        enableSorting: false,
        cell: ({ row }) => numberFormatter(row.original.nuQuantityQt2),
      },
    ],
    []
  )

  const columns = useMemo(
    () => withServiceMapColumns(baseColumns, hasServiceMap),
    [baseColumns, hasServiceMap]
  )

  const numberOfItems = listData?.count || 0
  const numberOfPages = Math.max(
    1,
    Math.ceil(numberOfItems / (pagination.pageSize || 1))
  )

  const choicesOfItemsPerPage = useMemo(
    () => [10, 20, 50].map(value => ({ label: String(value), value })),
    []
  )

  const choicesOfPageJump = useMemo(() => {
    const limit = Math.min(numberOfPages, 50)
    return Array.from({ length: limit }, (_, i) => ({
      label: String(i + 1),
      value: i,
    }))
  }, [numberOfPages])

  const handlePrevPage = () => {
    setPagination(p => ({ ...p, pageIndex: Math.max(p.pageIndex - 1, 0) }))
  }
  const handleNextPage = () => {
    setPagination(p => ({
      ...p,
      pageIndex: Math.min(p.pageIndex + 1, Math.max(0, numberOfPages - 1)),
    }))
  }
  const handleJumpToPage = (value: number) => {
    setPagination(p => ({ ...p, pageIndex: value }))
  }
  const handleChangeItemsPerPage = (value: number) => {
    setPagination(p => ({ pageIndex: 0, pageSize: value }))
  }

  return (
    <SurfaceCard padding={4}>
      <Stack spacing={4}>
        <DataFilter
          id="printing-card"
          filters={useMemo(() => {
            const dsCollectionCodes = serverFiltersData?.dsCollectionCodes || []
            const dsPublishers = serverFiltersData?.dsPublishers || []
            const coComponents = serverFiltersData?.coComponents || []

            const mapped: Array<IFilter> = [
              {
                type: 'select',
                name: 'dsCollectionCode',
                placeholder: 'Cod. da coleção',
                options: dsCollectionCodes.map(collectionCode => ({
                  label: collectionCode,
                  value: collectionCode,
                })),
              },
              {
                type: 'select',
                name: 'dsPublisher',
                placeholder: 'Editora',
                options: dsPublishers.map(publisher => ({
                  label: publisher,
                  value: publisher,
                })),
              },
              {
                type: 'select',
                name: 'coComponent',
                placeholder: 'Componente',
                options: coComponents.map(component => ({
                  label: component.label,
                  value: String(component.value),
                })),
              },
            ]

            return serviceMapFilters(mapped, serverFiltersData, hasServiceMap)
          }, [serverFiltersData, hasServiceMap])}
          hasDate={false}
          onChangeFilter={handleOnChangeFilter}
        />
        <HStack spacing={4}>
          <TotalizerDisplay
            number={totals.nuQuantityCensus}
            label="Total de livros | Base: Censo"
            icon="book"
            fullWidth
            small
          />
          <TotalizerDisplay
            variant="highlight"
            number={totals.nuQuantityQt1}
            label="Total de livros | Base: QT1"
            icon="book"
            fullWidth
            small
          />
          <TotalizerDisplay
            variant="highlight"
            number={totals.nuQuantityQt2}
            label="Total de livros | Base: QT2"
            icon="book"
            fullWidth
            small
          />
        </HStack>

        <HStack spacing={4} justifyContent="space-between">
          <Typography variant="h-xs" color="brand.primary.dark_2">
            Processamento por Tiragem
          </Typography>
          <Button
            variant="primary"
            label="Exportar"
            iconName="download"
            border="square"
            onClick={handleExport}
            isLoading={isExporting}
          />
        </HStack>
        {listData?.results.length ? (
          <>
            <ServiceMapTableScroll hasServiceMap={hasServiceMap}>
              <DataGrid
                columns={columns}
                data={listData?.results || []}
                hasPagination={false}
              />
            </ServiceMapTableScroll>
            <Pagination
              currentPageIndex={pagination.pageIndex}
              itemsPerPage={pagination.pageSize}
              numberOfItems={numberOfItems}
              numberOfPages={numberOfPages}
              choicesOfItemsPerPage={choicesOfItemsPerPage}
              choicesOfPageJump={choicesOfPageJump}
              onSelectPreviousPage={handlePrevPage}
              onSelectNextPage={handleNextPage}
              onJumpToPage={handleJumpToPage}
              onChangeItemsPerPage={handleChangeItemsPerPage}
            />
          </>
        ) : (
          <EmptyState
            title="Nenhuma coleção encontrada"
            description="Ajuste os filtros ou tente novamente mais tarde."
            shaped="false"
            iconProps={{
              name: 'table',
            }}
          />
        )}
      </Stack>
    </SurfaceCard>
  )
}

export default PrintingCard
